import { Combobox, ComboboxExtrasButton } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import React, { FC, ReactNode, memo, useCallback, useMemo } from 'react';

import { ApiExplorerFieldSpecialValuesDropdown } from './helpers/ApiExplorerFieldSpecialValuesDropdown';
import { usePrimitiveProps } from './hooks/nullable-hooks';
import { ApiExplorerFieldEntityProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

interface Props extends ApiExplorerFieldEntityProps {
  name: string;
  actionButton?: ReactNode | undefined;
  actionOnClear?: 'remove' | 'default-first' | 'empty-string';
}

const ApiExplorerEnumField: FC<Props> = memo(
  ({
    currentValue,
    onChange,
    path,
    entity,
    name,
    actionButton,
    // What action to do when the user deletes the full input. Useful for arrays
    actionOnClear = 'remove',
  }) => {
    const { isNullable } = useApiExplorerFieldContext();

    const values = useMemo(() => {
      return entity.enums
        .filter((enumItem) => enumItem.releaseStatus !== 'RETIRED')
        .map((enumItem) => ({
          value: enumItem.value,
          displayText: enumItem.value,
          description: enumItem.label ? (
            <Markdown
              markdown={enumItem.label}
              textColor="currentColor"
              textVariant="20"
            />
          ) : undefined,
        }));
    }, [entity.enums]);

    const onComboboxChange = useCallback(
      (value: string) => {
        if (value === '') {
          if (actionOnClear === 'default-first') {
            onChange(values[0]?.value ?? '', path);
          } else if (actionOnClear === 'empty-string') {
            onChange('', path);
          } else {
            onChange(undefined, path, 'remove');
          }
        } else {
          onChange(value, path);
        }
      },
      [actionOnClear, onChange, path, values]
    );

    const { placeholder, icon } = usePrimitiveProps(name, currentValue, false);

    return (
      <Combobox
        value={currentValue?.toString() || ''}
        placeholder={placeholder}
        leadingAccessory={icon}
        name={name}
        onChange={onComboboxChange}
        comboboxValues={values}
        actionButton={actionButton}
        extrasButton={
          <ApiExplorerFieldSpecialValuesDropdown
            currentValue={currentValue}
            showNullable={isNullable}
            showEmptyString={false}
            path={path}
            onChange={onChange}
            trigger={<ComboboxExtrasButton />}
          />
        }
      />
    );
  }
);

ApiExplorerEnumField.displayName = 'ApiExplorerEnumField';

export { ApiExplorerEnumField };
